import { Link } from "react-router-dom";
import { ArrowRight, Heart, Hammer, Phone, Users } from "lucide-react";
import { SITE } from "../lib/site";
import SectionHeader from "../components/SectionHeader";
import Seo from "../components/Seo";

const VALUES = [
    {
        icon: Phone,
        title: "We answer the phone",
        text: "No chasing, no ghosting. Dave or someone on the team picks up — and rings back if we miss it.",
    },
    {
        icon: Hammer,
        title: "We do the work ourselves",
        text: "No subbing your build out to strangers. Our team is on site, every day, in branded vans.",
    },
    {
        icon: Heart,
        title: "We treat your home like ours",
        text: "Tidy sites, dust sheets, hoovered up at the end of the day. Real respect for the people we work for.",
    },
    {
        icon: Users,
        title: "Friendly, local, accountable",
        text: "We live and work in the area. Half our jobs come from neighbours of past clients — that says it all.",
    },
];

export default function About() {
    return (
        <div data-testid="about-page">
            <Seo
                title="About Dandy Builders — A Friendly Local Builder in Wokingham"
                description="Founded in 2022 by Dave and the team, Dandy Builders & Property Care Ltd is a trusted local builder in Wokingham delivering extensions, lofts, groundworks and more across Berkshire."
                path="/about"
            />
            <section className="bg-muted border-b border-border">
                <div className="container-x py-20 md:py-28">
                    <SectionHeader
                        eyebrow="About Us"
                        title="Building dreams in Wokingham since 2022."
                        lead="Dandy Builders & Property Care is a friendly, family-feel construction company built around honest pricing, proper craftsmanship and a no-pressure approach. Whether it's a kitchen extension or a full period restoration, you get the same team start to finish."
                    />
                </div>
            </section>

            <section className="section-pad container-x grid md:grid-cols-12 gap-12 items-start">
                <div className="md:col-span-7 space-y-6">
                    <div className="eyebrow">Our Story</div>
                    <h2 className="text-3xl md:text-4xl font-heading font-extrabold tracking-tight">
                        A no-pressure approach. A no-shortcuts ethos.
                    </h2>
                    <div className="space-y-4 text-base md:text-lg text-muted-foreground leading-relaxed">
                        <p>
                            Dandy Builders & Property Care was founded in {SITE.foundedYear} in Wokingham, Berkshire.
                            We started because we wanted to do things the right way —
                            offer fixed-price quotes, turn up when we said we would, and leave a site cleaner than we found it.
                        </p>
                        <p>
                            What began as a small local team has grown into a fully-rounded firm covering everything from
                            foundations and groundworks through to electrical, mechanical and high-end finishes. We still
                            keep things friendly and personal — every job has someone on our team accountable from
                            first phone call to final handshake.
                        </p>
                        <p>
                            Whether you're upgrading your forever home, restoring a period property, or just need a
                            reliable builder for repairs and maintenance, we'd love to help.
                        </p>
                    </div>
                    <Link to="/contact" className="btn-primary mt-3" data-testid="about-contact-cta">
                        Get in touch <ArrowRight size={18} />
                    </Link>
                </div>
                <div className="md:col-span-5 space-y-4 sticky top-28">
                    <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/dandy-extension-foundations.jpg`}
                        alt="On-site at Dandy Builders"
                        className="w-full rounded-lg aspect-[4/5] object-cover"
                    />
                    <div className="grid grid-cols-2 gap-4">
                        <div className="bg-muted rounded-lg p-5 border border-border">
                            <div className="text-3xl font-heading font-extrabold text-primary">100+</div>
                            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-1">Projects delivered</div>
                        </div>
                        <div className="bg-muted rounded-lg p-5 border border-border">
                            <div className="text-3xl font-heading font-extrabold text-primary">5★</div>
                            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-1">Avg. client rating</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Meet the team */}
            <section className="section-pad bg-muted">
                <div className="container-x">
                    <SectionHeader eyebrow="Meet The Team" title="Run by people who pick up the phone." />
                    <div className="mt-14 grid md:grid-cols-3 gap-8">
                        <article className="card-earthy" data-testid="team-andy">
                            <img
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/dave-lyons.png`}
                                alt="Dave Lyons — Founder of Dandy Builders"
                                className="w-full aspect-[4/5] object-cover rounded-md"
                            />
                            <h3 className="mt-5 text-xl font-heading font-bold">Dave Lyons</h3>
                            <div className="text-sm text-secondary font-medium mt-1">Founder · Project Lead</div>
                            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                                Dave keeps every project on programme and is the first to roll up his sleeves
                                when needed. Brilliant problem-solver and relentless about client satisfaction.
                            </p>
                        </article>
                        <article className="card-earthy" data-testid="team-construction">
                            <div className="w-full aspect-[4/5] bg-primary/5 rounded-md grid place-items-center">
                                <Hammer size={80} strokeWidth={0.8} className="text-primary opacity-90" />
                            </div>
                            <h3 className="mt-5 text-xl font-heading font-bold">Construction Crew</h3>
                            <div className="text-sm text-secondary font-medium mt-1">Carpenters · Bricklayers · Groundworkers</div>
                            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                                Skilled tradespeople who turn drawings into reality. Tidy, professional, and on the
                                same job from start to finish — no rotating subcontractors.
                            </p>
                        </article>
                        <article className="card-earthy" data-testid="team-property-care">
                            <div className="w-full aspect-[4/5] bg-secondary/10 rounded-md grid place-items-center">
                                <Users size={80} strokeWidth={0.8} className="text-secondary opacity-90" />
                            </div>
                            <h3 className="mt-5 text-xl font-heading font-bold">Property Care Team</h3>
                            <div className="text-sm text-secondary font-medium mt-1">Electricians · Plumbers · Maintenance</div>
                            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                                Certified electricians and Gas Safe heating engineers handling reactive repairs,
                                routine inspections and full property maintenance for residential & commercial clients.
                            </p>
                        </article>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="section-pad container-x">
                <SectionHeader eyebrow="What you can expect" title="The Dandy difference." />
                <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {VALUES.map(({ icon: Icon, title, text }) => (
                        <div key={title} className="card-earthy" data-testid={`value-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                            <Icon className="text-primary" size={26} strokeWidth={1.5} />
                            <h3 className="mt-5 font-heading font-bold text-lg">{title}</h3>
                            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{text}</p>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
